/* eslint-disable max-lines-per-function */
import cx from 'classnames';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {Input} from '~library/atoms/Input';
import {resizeTextarea} from '~/SignUpSheets/utils/misc';
import {LinkPreview} from '~/SignUpSheets/components/LinkPreview/LinkPreview';
import {selectIsOrganizer, selectOrganizerName} from '~/SignUpSheets/reducers/sheet/selectors';
import {toTitleCase} from '~/common/fabric/CreateDesign/components/CardEditor/actions/TextFormat/components/Casing/Casing';
import {
  AffiliateIcons,
  generateDisplayText,
  isValidAmazonAffiliateUrl,
} from '~/SignUpSheets/utils/affiliate';
import {Button} from '~library/atoms/Button';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {ProductLinkInfoButton} from '~/SignUpSheets/components/ProductLinkInfoButton/ProductLinkInfoButton';

import {tanzanite} from '~libSass/base/_exports.sass';
import './LinkField.sass';

export const LinkField = ({
  onChange,
  onBlur,
  schema = {
    label_adornment: false,
    name: '',
    display_name: '',
    placeholder: '',
    required: false,
    max_chars: 200,
  },
  newOption = false,
  value = {url: '', display_text: ''},
  className,
  previewContainerClassName,
  errors,
  disabled,
  isInEditMode,
  preview = {
    affiliate_url: '',
    images: {
      primary: {
        small: '',
        medium: '',
        large: '',
      },
    },
    title: null,
    price: {
      display_amount: '',
    },
    vendor_name: null,
    url: '',
  },
  'data-qa-id': dataQaId,
  uuid,
  copied = false,
}) => {
  const linkRef = useRef(null);
  const displayTextRef = useRef(null);
  const userEditedDisplayTextRef = useRef(false);
  const linkChangedRef = useRef(false);
  const mounted = useRef(false);

  const [showInputs, setShowInputs] = useState(!isInEditMode && !preview.affiliate_url);
  const [pristineUrl, setPristineUrl] = useState(true);
  const [pristineDisplayText, setPristineDisplayText] = useState(true);
  const [shouldAutofillLinkText, setShowAutofillLinkText] = useState(newOption);
  const isOrganizer = useSelector(selectIsOrganizer);
  const organizerName = useSelector(selectOrganizerName);

  useEffect(() => {
    // Don't update pristine URL state if we're still focused in the field
    if (!linkRef.current || !newOption || linkRef.current === document.activeElement) return;
    if (value.url) setPristineUrl(false);
  }, [value, newOption]);

  useEffect(() => {
    // Don't update pristine URL state if we're still focused in the field
    if (!displayTextRef.current || !newOption || displayTextRef.current === document.activeElement)
      return;
    if (value.display_text) setPristineUrl(false);
  }, [value, newOption]);

  useEffect(() => {
    if (mounted.current) return;
    setShowAutofillLinkText(newOption || !value.url);
  }, [newOption, value.url]);

  useEffect(() => {
    if (!mounted.current) return;
    if (preview.vendor_name) {
      onChange({display_text: toTitleCase(preview.vendor_name)}, false);
      userEditedDisplayTextRef.current = false;
      return;
    }

    if (!showInputs || !linkRef.current) return;

    if (!shouldAutofillLinkText) userEditedDisplayTextRef.current = true;
    // Update or reset Display Text field
    if (preview.url && (linkRef.current.value === '' || !userEditedDisplayTextRef.current)) {
      const displayText = generateDisplayText(linkRef.current.value);
      onChange({display_text: displayText}, false);
    }

    // Data layer tracking
    if (linkChangedRef.current && linkRef.current?.value) {
      switch (preview.vendor_name) {
        case 'amazon': {
          // Amazon affiliate link
          window.dataLayer.push({
            event: 'sus-slot-amazon-link-added',
            url: preview.affiliate_url || preview.url,
          });
          break;
        }
        case null: {
          // Not an affiliate vendor
          window.dataLayer.push({
            event: 'sus-slot-link-added',
            url: preview.url,
          });
          break;
        }
        default: {
          // Some other valid affiliate vendor that is not Amazon
          window.dataLayer.push({
            event: 'sus-slot-other-link-added',
            url: preview.url,
          });
          break;
        }
      }
      linkChangedRef.current = false;
    }
  }, [preview.affiliate_url, preview.url, preview.vendor_name, showInputs, shouldAutofillLinkText]);

  useEffect(() => {
    resizeTextarea(linkRef.current);
    resizeTextarea(displayTextRef.current);
    if (mounted.current && showInputs && !copied) linkRef.current?.focus();
    mounted.current = true;
  }, [showInputs, copied, disabled]);

  useEffect(() => {
    setShowInputs(preview.affiliate_url && !preview.vendor_name);
  }, [preview.affiliate_url, preview.vendor_name]);

  const toggleEdit = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowInputs((ed) => !ed);
  }, []);

  const isAffiliateUrl = useMemo(() => !!preview.vendor_name, [preview]);

  useEffect(() => {
    if (
      isAffiliateUrl &&
      // If the user is currently editing the link again, don't kick them out of it
      document.activeElement !== linkRef.current
    ) {
      setShowInputs(false);
    }
  }, [preview]);

  const onEditDisplayText = useCallback(
    (e) => {
      userEditedDisplayTextRef.current = true;
      onChange({display_text: e.target.value});
    },
    [onChange]
  );

  const handleLinkInputChange = useCallback(
    (e) => {
      const newValue = e.target.value?.replace(/\s/g, '') ?? '';
      if (newValue === value?.url) return;
      linkChangedRef.current = true;
      onChange({url: newValue});
    },
    [onChange, value]
  );

  const handleLinkInputBlur = useCallback(
    (e) => {
      if (!e.target.value) return;
      setPristineUrl(false);
      if (!['https://', 'http://'].some((i) => e.target.value.startsWith(i))) {
        const url = `https://${e.target.value}`;
        onChange({url});
      }
      onBlur();
    },
    [onBlur, onChange]
  );

  const handleDisplayTextBlur = useCallback((e) => {
    if (e.target.value) setPristineDisplayText(false);
  }, []);

  const popoverButtonAvailable = schema.label_adornment && isOrganizer;

  const errorMessage = useMemo(() => {
    let message = errors?.[0]?.msg;
    if (typeof message === 'string') {
      try {
        message = JSON.parse(message);
      } catch (err) {
        message = {url: message};
      }
    }
    return message;
  }, [errors]);

  const EditLinkButton = useMemo(
    () =>
      disabled ? null : (
        <Button
          variant="transparent"
          onClick={toggleEdit}
          data-qa-id="edit-affiliate-url"
          className={cx('link-preview__edit-link', {
            'link-preview__edit-link__identified-product': !!preview.title,
          })}
        >
          <EditLineIcon ratio={0.5} color={tanzanite} />
        </Button>
      ),
    [disabled, toggleEdit, preview.title]
  );

  return (
    <div
      className={cx(`${schema.name}-wrapper`, {[className]: !!className, editing: {showInputs}})}
    >
      {preview.affiliate_url && !showInputs ? (
        <LinkPreview
          href={preview.affiliate_url}
          displayText={value?.display_text}
          image={preview.images?.primary?.medium?.url ?? AffiliateIcons.get(preview.vendor_name)}
          identifiedProduct={!!preview.title}
          title={
            preview.title ??
            `${organizerName} has a suggested purchase from ${toTitleCase(preview.vendor_name ?? '')}`
          }
          className={previewContainerClassName}
          titleVariant={isValidAmazonAffiliateUrl(preview) ? 'paragraph2' : 'paragraph3'}
          titleColor={preview.vendor_name ? 'pebble' : 'charcoal'}
          imgClassName={cx({'link-preview__image__mini-sq': !preview.title && isAffiliateUrl})}
          subtitle={preview.price?.display_amount}
          subtitleVariant="list2"
          vendor={preview.vendor_name}
          onEdit={toggleEdit}
          endAdornment={EditLinkButton}
        />
      ) : (
        <div
          className={cx('link-field__form', {
            'non-affiliate': preview.url && !isAffiliateUrl,
          })}
        >
          <div className="link-field__form__link-input-wrapper">
            <Input
              value={value?.url ?? ''}
              onChange={handleLinkInputChange}
              onBlur={handleLinkInputBlur}
              placeholder={schema.placeholder}
              maxLength={schema.max_chars}
              className={cx('link-field__form__url-field', {wide: !preview.url || isAffiliateUrl})}
              inputClassName="signup-sheets__text-textarea text__textarea"
              type="textarea"
              data-qa-id={`${dataQaId}-url`}
              onInput={() => resizeTextarea(linkRef.current)}
              rows={1}
              ref={linkRef}
              error={errorMessage?.url}
              disabled={disabled}
              live
              variant="paragraph2"
              pristine={pristineUrl}
            />
            {popoverButtonAvailable ? (
              <ProductLinkInfoButton dataQaId={dataQaId} uuid={uuid} />
            ) : null}
          </div>
          {preview.url && !isAffiliateUrl && (
            <Input
              value={value?.display_text ?? ''}
              onChange={onEditDisplayText}
              onBlur={handleDisplayTextBlur}
              maxLength={schema.max_chars}
              inputClassName="signup-sheets__text-textarea text__textarea"
              type="textarea"
              data-qa-id={`${dataQaId}-display-text`}
              onInput={() => resizeTextarea(displayTextRef.current)}
              rows={1}
              ref={displayTextRef}
              error={errorMessage?.display_text}
              disabled={disabled}
              live
              variant="paragraph2"
              pristine={pristineDisplayText}
            />
          )}
        </div>
      )}
    </div>
  );
};
